import React, { useState, useMemo, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, collectionGroup } from 'firebase/firestore';

function CourseStatistics() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [jumpToPage, setJumpToPage] = useState('');
  const coursesPerPage = 6;

  const fetchCourseStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setCurrentPage(1);

      // Fetch all courses once for titles/meta
      const coursesRef = collection(db, 'courses');
      const coursesSnapshot = await getDocs(coursesRef);
      const coursesData = coursesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      const initialStats = {};
      coursesData.forEach(course => {
        initialStats[course.id] = {
          title: course.title,
          totalEnrolled: 0,
          completedCount: 0,
          averageProgress: 0,
          moduleProgress: {},
          recentActivity: []
        };
      });

      // Fetch all course progress docs in a single collection group query
      const progressGroup = collectionGroup(db, 'courseProgress');
      const progressSnapshot = await getDocs(progressGroup);

      progressSnapshot.docs.forEach(docSnap => {
        const courseId = docSnap.id; // subcollection doc id is courseId
        const progressData = docSnap.data();
        const target = initialStats[courseId];
        if (!target) return;
        target.totalEnrolled += 1;
        target.averageProgress += progressData.progress || 0;
        // Count a user as completed if their recorded progress is 100%
        if ((progressData.progress || 0) >= 100) {
          target.completedCount += 1;
        }
      });

      Object.keys(initialStats).forEach(courseId => {
        const s = initialStats[courseId];
        if (s.totalEnrolled > 0) {
          s.averageProgress = Number((s.averageProgress / s.totalEnrolled).toFixed(1));
        }
      });

      setStats(initialStats);
    } catch (error) {
      console.error('Error fetching course statistics:', error);
      setError('Failed to load course statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Pagination logic
  const totalCourses = Object.keys(stats).length;
  const totalPages = Math.ceil(totalCourses / coursesPerPage);
  const startIndex = (currentPage - 1) * coursesPerPage;
  const endIndex = startIndex + coursesPerPage;
  const currentCourses = Object.entries(stats).slice(startIndex, endIndex);

  const handleJumpToPage = (e) => {
    e.preventDefault();
    const page = parseInt(jumpToPage);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setJumpToPage('');
    }
  };

  const SkeletonCard = useMemo(() => function SkeletonCard() {
    return (
      <div className="bg-gray-50 rounded-lg p-4 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-2/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-2/5"></div>
          <div className="h-2 bg-gray-200 rounded w-full mt-4"></div>
        </div>
      </div>
    );
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-800">Course Statistics</h3>
          <div className="text-sm text-gray-600">Loading...</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: coursesPerPage }).map((_, idx) => (
            <SkeletonCard key={idx} />
          ))}
        </div>
      </div>
    );
  }
  if (error) return <div className="text-red-500 p-4 bg-red-50 rounded-md">{error}</div>;
  if (Object.keys(stats).length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-800">Course Statistics</h3>
          <button
            onClick={fetchCourseStats}
            className="px-3 py-1 rounded-md bg-[rgb(130,88,18)] text-white hover:bg-[rgb(110,68,0)] disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Fetching...' : 'Fetch Course Statistics'}
          </button>
        </div>
        <div className="text-gray-600">Click the button to fetch the latest statistics.</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-800">Course Statistics</h3>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1}-{Math.min(endIndex, totalCourses)} of {totalCourses} courses
          </div>
          <button
            onClick={fetchCourseStats}
            className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {currentCourses.map(([courseId, courseStats]) => (
          <div key={courseId} className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-lg mb-3">{courseStats.title}</h4>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Enrolled:</span>
                <span className="font-semibold">{courseStats.totalEnrolled}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Completed:</span>
                <span className="font-semibold">{courseStats.completedCount}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Average Progress:</span>
                <span className="font-semibold">{courseStats.averageProgress}%</span>
              </div>
              
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className={`${courseStats.averageProgress < 30 ? 'bg-red-500' : courseStats.averageProgress < 70 ? 'bg-orange-400' : 'bg-green-600'} h-2.5 rounded-full`}
                    style={{ width: `${courseStats.averageProgress}%`, transition: 'width 0.5s ease-in-out' }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>

        <form onSubmit={handleJumpToPage} className="flex items-center gap-2">
          <label htmlFor="jumpToPage" className="text-sm text-gray-600">
            Jump to:
          </label>
          <input
            type="number"
            id="jumpToPage"
            min="1"
            max={totalPages}
            value={jumpToPage}
            onChange={(e) => setJumpToPage(e.target.value)}
            className="w-16 px-2 py-1 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)] focus:border-[rgb(130,88,18)]"
            placeholder="Page"
          />
          <button
            type="submit"
            className="px-3 py-1 rounded-md bg-[rgb(130,88,18)] text-white hover:bg-[rgb(110,68,0)]"
          >
            Go
          </button>
        </form>
      </div>
    </div>
  );
}

export default CourseStatistics; 